import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { faArrowCircleLeft, faTicket } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import {
  ChangeTicketResponse,
  DynamicAttraction,
  Gross,
} from 'ts/dynamic/interfaces/DynamicResponse';
import { dateFormat } from 'utils/dynamic/i18n';
import { formatPriceDynamic } from 'utils/dynamic/dynamic';
import { getCurrency, showError } from 'utils/utils';
import { changeTicket } from 'services/dynamic';
import useRecaptcha from 'hooks/useRecaptcha';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface ChangeAttractionProps {
  onPressBack: () => void;
  dynamicAttraction: DynamicAttraction;
  searchToken: string;
  onSelectNewTicket: (res: ChangeTicketResponse) => void;
  currency: keyof Gross;
  isTicketOnly?: boolean;
}

const ChangeAttraction: FC<ChangeAttractionProps> = ({ dynamicAttraction: dyn, ...props }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { handleRecapcha } = useRecaptcha();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const replaceDesc = (desc: string) => {
    const first = desc.split('-')[0];
    const rest = desc.split('-').slice(1).join('<br>');

    return `<span class="text-md text-bold text-contrast">${first}</span><br>` + rest;
  };

  const onClickChange = async (id: number) => {
    if (id === dyn.id) {
      props.onPressBack();
      return;
    }

    setIsLoading(true);
    const token = await handleRecapcha();
    if (!token) return;

    const response = await changeTicket(token, props.searchToken, id);

    if (response && !response.hasErrors && response.tiketChanged) {
      props.onSelectNewTicket({ ...response, id });
    } else {
      console.log('Dynamic/ChangeTicket ' + response?.errorMessage || 'Error');
      showError(t('something_went_wrong'));
    }

    setIsLoading(false);
  };

  return (
    <div style={{ minHeight: 'unset' }}>
      <div className="flex-one">
        {!props.isTicketOnly && (
          <div className="mb-20 default-shadow flex-between dyn-change-header">
            <Button
              title={t('back')}
              icon={faArrowCircleLeft}
              onClick={props.onPressBack}
              className="btn btn-link"
            />
          </div>
        )}

        <div className="default-shadow details-block">
          <div className="title title-header-grey">
            <FontAwesomeIcon icon={faTicket} className="mr-5" /> {t('dyn.ticket')}
          </div>
          <div className="flex-row">
            <div className="dyn-details-tickets">
              <div className="dyn-details-tickets-row">
                <div className="dyn-details-tickets-title">{t('dyn.supplement_card')}</div>
                <div className="dyn-details-tickets-actions">{t('dyn.card_category')}</div>
              </div>

              {dyn.tickets.map((ticket, index) => (
                <div
                  className={
                    'dyn-details-tickets-row' +
                    (dyn.id === ticket.ticket.itemNumber ? ' ticket-choosen' : '')
                  }
                  key={index}
                >
                  <div className="dyn-details-tickets-title">
                    <div
                      dangerouslySetInnerHTML={{ __html: replaceDesc(ticket.ticket.ticketDesc) }}
                    ></div>
                  </div>
                  <div className="dyn-details-tickets-actions">
                    {formatPriceDynamic(
                      (ticket.ticket.myPrice.gross[props.currency] as number) -
                        (dyn.price.gross[props.currency] as number),
                      getCurrency(props.currency)
                    )}
                    <span>
                      <Button
                        disabled={isLoading}
                        title={
                          dyn.id === ticket.ticket.itemNumber ? t('dyn.your_choice') : t('choose')
                        }
                        onClick={() => onClickChange(ticket.ticket.itemNumber)}
                        className="btn btn-sm btn-primary"
                      />
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className="dyn-details-info">
              <div>
                <span className="text-contrast">{dyn.showName}</span>
                <span className="text-light text-xsm"> - {dyn.categoryDesc}</span>
              </div>
              <div>
                <span>{dyn.ticketDesc}</span>
              </div>
              <div>{dateFormat(dyn.eventDate, 'dd/MM/yyyy - HH:mm')}</div>
              <div>
                {dyn.city}, {dyn.country} - {dyn.vanueDesc}
              </div>

              {dyn?.categoryImage?.imageLink && (
                <div className="w100p flex-center mt-10 mb-10">
                  <img
                    src={dyn.categoryImage.imageLink}
                    alt="1"
                    style={{ maxWidth: 150, textAlign: 'center' }}
                  />
                </div>
              )}
              <div className="">
                <img src={dyn.image} alt="ticket" />
              </div>
              <div className="text-light text-xsm">
                <span className="text-contrast">*</span>
                <span>{t('dyn.image_for_illustration')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(ChangeAttraction, 'ChangeAttraction');
